'use client';

import { usePathname } from 'next/navigation';
import Link from 'next/link';
import React from 'react';

import { useSlugContext } from '@/app/context/SlugContext';
import { i18n } from '@/i18n.config';
import { cn } from '@/lib/utils';

export function LocaleSwitcher({ currentLocale }: { currentLocale: string }) {
  const { state } = useSlugContext();
  const { localizedSlugs } = state;

  const pathname = usePathname(); // Current path

  // Generate localized path for each locale
  const generateLocalizedPath = (locale: string): string => {
    if (!pathname) return `/${locale}`; // Default to root path for the locale
    const segments = pathname.split('/'); // Split path into segments

    // Handle homepage (e.g., "/en" -> "/fr")
    if (segments.length <= 2) {
      return `/${locale}`;
    }

    // Handle dynamic paths (e.g., "/en/blog/[slug]")
    if (localizedSlugs[locale]) {
      const newSegments = [...segments];
      newSegments[1] = locale; // Replace the locale
      newSegments[newSegments.length - 1] = localizedSlugs[locale]; // Replace slug if available
      return newSegments.join('/');
    }

    // Fallback to replace only the locale
    const newSegments = [...segments];
    newSegments[1] = locale;
    return newSegments.join('/');
  };

  return (
    <div className="flex gap-2 p-1 rounded-md">
      {i18n.locales.map((locale) => (
        <Link key={locale} href={generateLocalizedPath(locale)}>
          <div
            className={cn(
              'flex cursor-pointer items-center justify-center text-sm leading-[110%] w-8 py-1 rounded-md hover:bg-neutral-100 hover:text-black text-black transition duration-200',
              locale === currentLocale
                ? 'bg-secondary text-white shadow-sm'
                : ''
            )}
          >
            {locale}
          </div>
        </Link>
      ))}
    </div>
  );
}
